import { getChannel, getBrowserEnv } from "@/utils/tools";
import { goToPageV2, debounce, NativeByUrlList } from "@/utils/common";
import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  CSSProperties,
  ReactNode,
  FC,
  useMemo,
} from "react";
import { trackGdp } from "@/utils/gio-report";

/** 位置配置接口：定义浮动按钮的初始位置策略 */
interface PositionConfig {
  /** 距离左侧距离（优先级高于right） */
  left?: number;
  /** 距离顶部距离（优先级高于bottom） */
  top?: number;
  /** 距离右侧距离（默认20） */
  right?: number;
  /** 距离底部距离（默认20） */
  bottom?: number;
}

/** 组件属性接口 */
interface FloatingButtonProps {
  /** 初始位置配置 */
  initialPosition?: PositionConfig;
  /** 背景图片URL */
  backgroundImage?: string;
  /** 自定义样式 */
  style?: CSSProperties;
  /** 子元素内容 */
  children?: ReactNode;
  /** 拖拽结束回调函数 */
  onDragEnd?: (position: { x: number; y: number }) => void;
  /** 是否启用拖拽边界限制（默认true） */
  dragBoundary?: boolean;
  /** 是否允许拖拽（默认true） */
  draggable?: boolean;
  link?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  activityInfo: Object;
}

/** 拖拽状态接口：记录拖拽过程中的状态信息 */
interface DragState {
  /** 是否正在拖拽 */
  isDragging: boolean;
  /** 拖拽起点X坐标 */
  startX: number;
  /** 拖拽起点Y坐标 */
  startY: number;
  /** 原始位置X坐标 */
  originX: number;
  /** 原始位置Y坐标 */
  originY: number;
  /** 事件类型：触摸或鼠标 */
  type: "touch" | "mouse" | null;
}

/** 基础样式配置 */
const BASE_STYLE: CSSProperties = {
  position: "fixed",
  width: 50,
  height: 50,
  // borderRadius: '50%',
  backgroundSize: "cover",
  backgroundPosition: "center",
  touchAction: "none", // 禁用浏览器默认触摸行为
  userSelect: "none", // 禁止文本选择
  zIndex: 999,
};

const FloatingButton: FC<FloatingButtonProps> = ({
  initialPosition = {},
  backgroundImage,
  style,
  children,
  onDragEnd,
  dragBoundary = true,
  draggable = true,
  link,
  onClick,
  activityInfo,
}) => {
  /** 按钮DOM引用 */
  const btnRef = useRef<HTMLDivElement>(null);
  const propsInitialPosition = useMemo(
    () => ({
      left: initialPosition.left,
      top: initialPosition.top,
      right: initialPosition.right,
      bottom: initialPosition.bottom,
    }),
    [
      initialPosition.bottom,
      initialPosition.left,
      initialPosition.right,
      initialPosition.top,
    ],
  );
  /** 位置状态（用于渲染） */
  const [position, setPositionState] = useState({ x: 0, y: 0 });
  /** 位置引用（用于事件回调获取最新值） */
  const positionRef = useRef(position);
  /** 拖拽状态引用 */
  const dragState = useRef<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    type: null,
  });

  /** 同步更新位置状态和引用 */
  const updatePosition = (newPosition: { x: number; y: number }) => {
    positionRef.current = newPosition; // 更新引用值
    setPositionState(newPosition); // 更新状态触发渲染
  };

  useEffect(() => {
    /** 计算初始位置 */
    const calcPosition = () => {
      const { left, top, right = 20, bottom = 20 } = propsInitialPosition;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // 优先使用绝对定位（left/top）
      if (left !== undefined || top !== undefined) {
        return { x: left ?? 0, y: top ?? 0 };
      }

      // 智能定位：默认显示在右下角
      const btnWidth = btnRef.current?.offsetWidth ?? 50;
      const btnHeight = btnRef.current?.offsetHeight ?? 50;
      return {
        x: viewportWidth - btnWidth - (right ?? 20),
        y: viewportHeight - btnHeight - (bottom ?? 20),
      };
    };

    updatePosition(calcPosition());
  }, [propsInitialPosition]);
  useEffect(() => {
    track();
  }, []);
  //埋点-组件曝光
  const track = () => {
    trackGdp("P20189", "B003", {
      trackName: "充电_营销专题页_组件曝光",
      origin: getChannel(),
      userType: "",
      versionId: activityInfo?.versionCode,
      pageId: activityInfo?.pageCode,
      ComponentCode: activityInfo?.moduleId,
      description: activityInfo?.moduleRemark,
      type: activityInfo?.moduleType,
      activityId: activityInfo?.connectedActivitiesId,
      index: "",
    });
  };

  /** 处理拖拽开始 */
  const handleDragStart = useCallback(
    (clientX: number, clientY: number, type: DragState["type"]) => {
      // 记录拖拽初始状态
      dragState.current = {
        isDragging: true,
        startX: clientX,
        startY: clientY,
        originX: positionRef.current.x,
        originY: positionRef.current.y,
        type,
      };
    },
    [],
  );

  /** 处理拖拽移动 */
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragState.current.isDragging) return;

      // 计算偏移量
      const deltaX = clientX - dragState.current.startX;
      const deltaY = clientY - dragState.current.startY;
      let newX = dragState.current.originX + deltaX;
      let newY = dragState.current.originY + deltaY;

      // 边界限制处理
      if (dragBoundary && btnRef.current) {
        const btnRect = btnRef.current.getBoundingClientRect();
        const { isXPApp } = getBrowserEnv();
        let minY = 0;
        if (isXPApp) {
          const navbar = document.getElementsByClassName("navbar");
          if (navbar && navbar?.[0]) {
            minY = navbar[0]?.offsetTop + 44;
          }
        }
        newX = Math.max(0, Math.min(newX, window.innerWidth - btnRect.width));
        newY = Math.max(
          isXPApp ? minY : 0,
          Math.min(newY, window.innerHeight - btnRect.height),
        );
      }

      updatePosition({ x: newX, y: newY });
    },
    [dragBoundary],
  );

  /** 创建通用移动事件处理器 */
  const createMoveHandler = useCallback(
    (type: "touch" | "mouse") => (e: TouchEvent | MouseEvent) => {
      let clientX: number, clientY: number;

      // 处理不同类型事件
      if (type === "touch") {
        const touchEvent = e as TouchEvent;
        if (!touchEvent.touches?.[0]) return;
        ({ clientX, clientY } = touchEvent.touches[0]);
      } else {
        const mouseEvent = e as MouseEvent;
        ({ clientX, clientY } = mouseEvent);
      }

      handleDragMove(clientX, clientY);
    },
    [handleDragMove],
  );

  /** 启动拖拽流程 */
  const startDrag = useCallback(
    (clientX: number, clientY: number, type: "touch" | "mouse") => {
      if (!draggable) return;

      // 初始化拖拽状态
      handleDragStart(clientX, clientY, type);

      // 创建事件处理器
      const handleMove = createMoveHandler(type);
      const handleEnd = () => {
        dragState.current.isDragging = false;
        onDragEnd?.(positionRef.current); // 使用ref获取最新位置
        document.removeEventListener(`${type}move`, handleMove);
        document.removeEventListener(`${type}end`, handleEnd);
      };

      document.addEventListener(`${type}move`, handleMove);
      document.addEventListener(`${type}end`, handleEnd);
    },
    [draggable, handleDragStart, createMoveHandler, onDragEnd],
  );

  /** 触摸事件入口 */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches[0]) {
        const { clientX, clientY } = e.touches[0];
        startDrag(clientX, clientY, "touch");
      }
    },
    [startDrag],
  );

  /** 鼠标事件入口 */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ("ontouchstart" in window) {
        return;
      }
      // 防止重复拖拽
      if (!dragState.current.isDragging) {
        startDrag(e.clientX, e.clientY, "mouse");
      }
    },
    [startDrag],
  );

  const handleClick = debounce((e: React.MouseEvent<HTMLDivElement>) => {
    // 检查是否有新的 jumpUrlMap 格式
    const baseData = activityInfo?.moduleJsonData?.base?.[0];
    if (baseData?.jumpUrlMap) {
      // 使用新的 jumpUrlMap 格式
      NativeByUrlList(baseData.jumpUrlMap);
    } else if (link) {
      // 兜底使用旧的 link 格式
      if (link.includes(window.location.origin)) {
        window.location.href = link;
      } else {
        goToPageV2({ url: link });
      }
    }

    onClick?.(e);
    trackGdp("P20189", "B004", {
      trackName: "充电_营销专题页_组件点击",
      origin: getChannel(),
      userType: "",
      versionId: activityInfo.versionCode,
      pageId: activityInfo.pageCode,
      ComponentCode: activityInfo.moduleId,
      description: activityInfo.moduleRemark,
      type: activityInfo.moduleType,
      activityId: activityInfo.connectedActivitiesId,
      clickLocation: "悬浮窗点击",
    });
  }, 500);

  return (
    <div
      ref={btnRef}
      onTouchStart={handleTouchStart}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{
        ...BASE_STYLE,
        left: position.x,
        top: position.y,
        cursor: draggable ? "grab" : "default",
        backgroundImage: backgroundImage && `url(${backgroundImage})`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default FloatingButton;
